import { useMutation, useQueryClient } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import { invalidateContentArtifacts } from '@/enterprise/data/useArtifacts';
import { DraftOfferType, DraftType } from '@/enterprise/data/useDraftOffer';
import { OfferConfig, RenewalInformation, TakerMarket } from '@/enterprise/data/useTakerMarkets';
import apiClient from '@/lib/apiClient';
import { usePendingOfferPollingStore } from '@/lib/offerStatusPolling/usePendingOfferPollingStore';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import { useReporting } from '@/reporting';
import useExchangeRates from '@/shared/data/useExchangeRates';
import { OfferBuilderFrequency } from '../../OfferBuilderContext';
import { computeMarketSubmitStats, trackOfferSubmitEvent } from '../../utils';

type SubmitDraftOfferRequestBody = paths['/offers/draft/{uuid}']['post']['requestBody']['content']['application/json'];
export type SubmitDraftOfferRateInformations = SubmitDraftOfferRequestBody['rateInformations'];
type SubmitDraftRateInformation = NonNullable<SubmitDraftOfferRateInformations>[number]['rateInformation'];
type OfferType = SubmitDraftOfferRequestBody['offerType'];
type Referrer = SubmitDraftOfferRequestBody['referrer'];

type SubmitDraftResponse201 = paths['/offers/draft/{uuid}']['post']['responses']['201']['content']['application/json'];
type SubmitDraftResponse202 = paths['/offers/draft/{uuid}']['post']['responses']['202']['content']['application/json'];

// Minimal shape needed by onSuccess to resolve offer UUIDs per taker market
interface NormalizedDraftOffer {
  uuid: string;
  supplierMarkets: { marketUuid: string; supplierDivisionUuid: string }[];
}

async function submitDraftOfferSEA({
  uuid,
  offerType,
  rateInformations,
  renewalInformation,
  referrer,
}: {
  uuid: string;
  offerType: OfferType;
  rateInformations?: SubmitDraftOfferRateInformations;
  renewalInformation?: RenewalInformation;
  referrer?: Referrer;
}): Promise<{ status: number; data: NormalizedDraftOffer[] | undefined }> {
  const response = await apiClient.post(`api/supplier-experience/offers/draft/${uuid}`, {
    json: {
      offerType,
      renewalInformation,
      rateInformations,
      referrer,
    },
  });

  if (response.status === 201) {
    const body = await response.json<SubmitDraftResponse201>();
    return { status: response.status, data: body.data };
  }

  if (response.status === 202) {
    const body = await response.json<SubmitDraftResponse202>();
    return {
      status: response.status,
      data: body.data.offers.map((o) => ({
        uuid: o.uuid,
        supplierMarkets: [{ marketUuid: o.marketUuid, supplierDivisionUuid: o.supplierDivisionUuid }],
      })),
    };
  }

  return { status: response.status, data: undefined };
}

export interface SubmitDraftOfferInput {
  draftOfferUuid?: string;
  draftOfferType?: DraftOfferType;
  draftType: DraftType;
  draftSource?: string | null;
  rateInformations?: SubmitDraftOfferRateInformations;
  renewalInformation?: RenewalInformation;
  referrer?: Referrer;
  termExpirationDate?: string;
  frequency: OfferBuilderFrequency;
  takerMarkets: TakerMarket[];
  // stats for track event only
  currency: string;
  totalInvoiceAmount: number;
  totalInvoiceAmountUsd: number;
  totalDiscount: number;
  totalDiscountUsd: number;
  totalEarlyPaymentAmount: number;
  totalEarlyPaymentAmountUsd: number;
  totalInvoiceCount: number;
}

const getOfferType = (draftOfferType: DraftOfferType): OfferType => {
  switch (draftOfferType) {
    case 'PREFERRED':
      return 'preferred-term';
    case 'PRICE_DISCOVERY':
      return 'price-discovery';
    case 'EXPRESS_ACCEPT':
      return 'express-accept';
    case 'BENCHMARK':
      return 'benchmark';
    case 'STATIC':
      return 'static';
    default:
      throw new Error(`Unsupported draft offer type: ${draftOfferType}`);
  }
};

/**
 * Merges optimistic `offerConfig` after a successful `submitDraftOfferSEA` call.
 */
export function mergeOfferConfig({
  prevConfig,
  draftOfferType,
  matchRateInfo,
  frequency,
  offerUuid,
}: {
  prevConfig: OfferConfig;
  draftOfferType: DraftOfferType;
  matchRateInfo: SubmitDraftRateInformation | undefined;
  frequency: OfferBuilderFrequency;
  offerUuid?: string;
}): OfferConfig {
  const nextFrequency = frequency === 'oneTime' ? 'ONE_TIME' : 'RECURRING';

  const result: OfferConfig = {
    ...prevConfig,
    uuid: offerUuid,
    isEnabled: true,
    frequency: nextFrequency,
  };

  if (draftOfferType === 'BENCHMARK') {
    return result;
  }

  if (matchRateInfo?.rateType === 'REF') {
    return {
      ...result,
      isDiscountBidding: false,
      isReferenceRateBidding: true,
      offerType: draftOfferType === 'PREFERRED' ? 'PREFERRED_TERM' : undefined,
    };
  }

  // Static, Price Discovery, Express Accept, and Preferred on Price Discovery markets handle rates the same way
  const maxApr = matchRateInfo?.rateType === 'APR' && 'rate' in matchRateInfo ? matchRateInfo.rate : null;
  const maxDiscount = matchRateInfo?.rateType === 'DISC' && 'rate' in matchRateInfo ? matchRateInfo.rate : null;
  const isDiscountBidding = matchRateInfo?.rateType === 'DISC';

  return {
    ...result,
    maxApr,
    maxDiscount,
    isDiscountBidding,
    isReferenceRateBidding: false,
    isEnabled: true,
    // Match SEA offer type mapping from convertSEAOfferType
    offerType:
      draftOfferType === 'PREFERRED'
        ? 'PREFERRED_TERM'
        : draftOfferType === 'EXPRESS_ACCEPT'
        ? 'EXPRESS_ACCEPT'
        : undefined,
    isManuallyAwarded: draftOfferType === 'EXPRESS_ACCEPT' ? true : false,
  };
}

/**
 * Hook for submitting draft offers
 * Returns a mutation that handles validation errors internally
 * and triggers the onError handler without requiring a catch in the component
 */
const useSubmitDraftOffer = () => {
  const queryClient = useQueryClient();
  const { refetchManyTakerMarkets } = useRefetchStatsQueries();
  const { trackEnterpriseEvent } = useReporting();
  const { data: exchangeRates } = useExchangeRates();
  const { addPendingMarkets } = usePendingOfferPollingStore();

  const mutation = useMutation({
    mutationKey: ['submitOfferFromDraft'],
    mutationFn: async ({
      draftOfferUuid,
      draftOfferType,
      draftType,
      draftSource,
      rateInformations,
      renewalInformation,
      referrer,
    }: SubmitDraftOfferInput) => {
      trackEnterpriseEvent('draft-offer::submit', { draftOfferUuid, type: draftOfferType, draftType, draftSource });

      if (!draftOfferUuid || !draftOfferType) {
        throw new Error(
          `Draft offer information is required from draftOfferUuid: ${draftOfferUuid}, draftOfferType: ${draftOfferType}`
        );
      }

      if (
        draftOfferType !== 'PRICE_DISCOVERY' &&
        draftOfferType !== 'PREFERRED' &&
        draftOfferType !== 'EXPRESS_ACCEPT' &&
        draftOfferType !== 'BENCHMARK' &&
        draftOfferType !== 'STATIC'
      ) {
        throw new Error(
          `Only price discovery, preferred term, benchmark, express accept, and static offer types are supported but ${draftOfferType} was provided`
        );
      }

      if (!rateInformations || rateInformations.length === 0) {
        throw new Error('Rate information is required for draft offers');
      }

      if (draftOfferType === 'PRICE_DISCOVERY') {
        const hasRefRate = rateInformations.some((ri) => ri.rateInformation?.rateType === 'REF');
        if (hasRefRate) {
          throw new Error('Only APR and DISC rate types are supported for draft price discovery offers');
        }
      }

      return submitDraftOfferSEA({
        uuid: draftOfferUuid,
        offerType: getOfferType(draftOfferType),
        rateInformations,
        renewalInformation,
        referrer,
      });
    },
    onSuccess: (
      { status, data: submittedOffers },
      {
        draftOfferUuid,
        draftOfferType,
        draftType,
        draftSource,
        rateInformations,
        renewalInformation,
        referrer,
        takerMarkets,
        termExpirationDate,
        frequency,
        currency,
        totalInvoiceAmount,
        totalInvoiceAmountUsd,
        totalDiscount,
        totalDiscountUsd,
        totalEarlyPaymentAmount,
        totalEarlyPaymentAmountUsd,
        totalInvoiceCount,
      }
    ) => {
      const submissionBatchId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      trackEnterpriseEvent('draft-offer::submit::success', {
        draftOfferUuid,
        type: draftOfferType,
        draftType,
        draftSource,
        referrer: referrer?.workflow ?? 'offer-builder',
        currency,
        totalInvoiceAmount,
        totalInvoiceAmountUsd,
        totalDiscount,
        totalDiscountUsd,
        totalEarlyPaymentAmount,
        totalEarlyPaymentAmountUsd,
        totalInvoiceCount,
        submissionBatchId,
      });

      if (takerMarkets && takerMarkets.length && draftOfferType) {
        takerMarkets.forEach((tm) => {
          const matchRateInfo = rateInformations?.find(
            (ri) => ri.marketUuid === tm.marketUuid && ri.supplierDivisionUuid === tm.takerDivisionUuid
          )?.rateInformation;

          const rateType =
            matchRateInfo?.rateType === 'DISC' || matchRateInfo?.rateType === 'APR' ? matchRateInfo.rateType : null;
          const rate = matchRateInfo && rateType && 'rate' in matchRateInfo ? matchRateInfo.rate : null;

          const marketStats =
            rateType && rate != null
              ? computeMarketSubmitStats({ takerMarket: tm, rate, rateType, exchangeRates })
              : {};

          const pricingStrategy = draftOfferType === 'EXPRESS_ACCEPT' ? 'expressAccept' : undefined;

          trackOfferSubmitEvent({
            pricingType: draftOfferType,
            marketUuid: tm.marketUuid,
            supplierDivisionUuid: tm.takerDivisionUuid,
            submissionBatchId,
            isDraft: true,
            isEdit: false,
            isEnabled: true,
            isManuallyAwarded: tm.offerConfig.isManuallyAwarded ?? false,
            marketStats,
            extras: {
              draftOfferUuid,
              draftType,
              draftSource,
              ...(pricingStrategy && { pricingStrategy }),
              ...(draftOfferType === 'PREFERRED' && { autoRenewalEnabled: renewalInformation?.enabled ?? false }),
            },
            trackEnterpriseEvent,
          });
        });
      }

      if (takerMarkets && takerMarkets.length && draftOfferType) {
        takerMarkets.forEach((effectedTm) => {
          // update the cache with the returned data
          queryClient.setQueryData<TakerMarket[]>(['taker-markets'], (prevTakerMarkets) => {
            return (prevTakerMarkets ?? []).map((prevTm) => {
              const isMatch =
                prevTm.marketUuid === effectedTm.marketUuid &&
                prevTm.takerDivisionUuid === effectedTm.takerDivisionUuid;

              if (!isMatch) {
                return prevTm;
              }

              const matchRateInfo: SubmitDraftRateInformation | undefined = rateInformations?.find(
                (rateInfo) =>
                  rateInfo.marketUuid === effectedTm.marketUuid &&
                  rateInfo.supplierDivisionUuid === effectedTm.takerDivisionUuid
              )?.rateInformation;

              const matchOffer = submittedOffers?.find((offer) =>
                offer.supplierMarkets.some(
                  (market) =>
                    market.marketUuid === effectedTm.marketUuid &&
                    market.supplierDivisionUuid === effectedTm.takerDivisionUuid
                )
              );

              const offerConfig = mergeOfferConfig({
                prevConfig: prevTm.offerConfig,
                draftOfferType,
                matchRateInfo,
                frequency,
                offerUuid: matchOffer?.uuid,
              });

              return {
                ...prevTm,
                offerConfig,
                ...(termExpirationDate && { disableAfterMarketClearsDate: termExpirationDate }),
              };
            });
          });
        });

        if (status === 202) {
          addPendingMarkets(
            takerMarkets.map((effectedTm) => ({
              marketUuid: effectedTm.marketUuid,
              takerDivisionUuid: effectedTm.takerDivisionUuid,
              makerOrganizationUuid: effectedTm.makerOrganizationUuid,
            }))
          );
        } else {
          // offer resolved synchronously, refetch immediately
          takerMarkets.forEach((effectedTm) => {
            trackEnterpriseEvent('polling-update::in-time', {
              marketUuid: effectedTm.marketUuid,
              takerDivisionUuid: effectedTm.takerDivisionUuid,
              makerOrganizationUuid: effectedTm.makerOrganizationUuid,
            });
          });
          refetchManyTakerMarkets(
            takerMarkets.map((effectedTm) => ({
              marketUuid: effectedTm.marketUuid,
              takerUuid: effectedTm.takerDivisionUuid,
            }))
          );
        }
      }

      // Marketing artifacts can display messages promoting offers.
      // Invalidate so we stop marketing offers the supplier just submitted.
      invalidateContentArtifacts(queryClient);
    },
    onError: (error, { draftOfferUuid, draftOfferType, draftType, draftSource }) => {
      trackEnterpriseEvent('draft-offer::submit::error', {
        draftOfferUuid,
        type: draftOfferType,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        draftType,
        draftSource,
      });
    },
  });

  // Wrap the mutateAsync function to handle errors internally
  const wrapper = async (...args: Parameters<typeof mutation.mutateAsync>) => {
    try {
      return await mutation.mutateAsync(...args);
    } catch {
      return undefined;
    }
  };

  return {
    ...mutation,
    mutateAsync: wrapper,
  };
};

export default useSubmitDraftOffer;
